import { Link, useRouterState } from "@tanstack/react-router";
import { Car, Menu, Phone, UserRound } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Logo } from "@/components/site/Logo";
import { FlagNL } from "@/components/site/FlagIcon";
import { nlPathForEn } from "@/lib/i18n-urls";
import { trackBookCtaClick, trackPhoneClick } from "@/lib/tracking";
import { useCustomerLocale } from "@/hooks/use-customer-locale";
import { toast } from "sonner";

const MAIN_NAV = [
  { to: "/en/book", label: "Book a taxi" },
  { to: "/en/prices", label: "Prices" },
  { to: "/en/airport-taxi", label: "Airport transfers" },
  { to: "/en/how-it-works", label: "How it works" },
  { to: "/en/contact", label: "Contact" },
] as const;

/** Taalwissel: gaat altijd naar de exacte Nederlandse tegenhanger. */
function LanguageSwitch({ className }: { className?: string }) {
  const { setLocale } = useCustomerLocale();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nl = nlPathForEn(pathname) ?? "/";
  return (
    <a
      href={nl}
      onClick={() => { void setLocale("nl").catch(() => toast.error("Language preference could not be saved.")); }}
      hrefLang="nl-NL"
      aria-label="Switch to Dutch"
      className={
        className ??
        "inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-secondary hover:text-foreground"
      }
    >
      <FlagNL />
      <span>NL</span>
    </a>
  );
}

export function EnHeader() {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-border/80 bg-background/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/en" className="flex items-center">
          <Logo priority />
        </Link>

        <nav aria-label="Main navigation" className="hidden items-center gap-1 lg:flex">
          {MAIN_NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              activeProps={{ className: "text-foreground bg-secondary" }}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitch />
          <Link
            to="/en/customer/login"
            className="hidden items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground md:inline-flex"
          >
            <UserRound className="size-4" /> My rides
          </Link>
          <Button asChild size="sm" className="font-semibold">
            <Link to="/en/book" onClick={() => trackBookCtaClick("header")}>
              Book now
            </Link>
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto">
              <div className="mt-8 flex flex-col gap-1">
                {MAIN_NAV.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-3 py-2.5 text-base font-medium hover:bg-secondary"
                  >
                    {item.label}
                  </Link>
                ))}
                <Link
                  to="/en/customer/login"
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-base font-medium hover:bg-secondary"
                >
                  My rides
                </Link>
                <Link
                  to="/en/partners"
                  onClick={() => setOpen(false)}
                  className="mt-4 rounded-lg border-t border-border px-3 pb-2.5 pt-4 text-base font-medium text-muted-foreground hover:bg-secondary"
                >
                  For drivers
                </Link>
                <Link
                  to="/partner/login"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-base font-medium text-muted-foreground hover:bg-secondary"
                >
                  <Car className="size-4" /> Driver login
                </Link>
                <a
                  href="tel:+31850607086"
                  onClick={() => trackPhoneClick("header")}
                  className="mt-4 flex items-center gap-2 border-t border-border px-3 pt-4 text-base font-medium text-muted-foreground"
                >
                  <Phone className="size-4" /> +31 85 060 7086
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
